import i18n from '@/utils/i18n';
import React, { useCallback, useState } from 'react';
import validator from 'validator';
import { clampQuantityToLimits } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-utils';
import { ColumnId } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/constants';
import { createStyles } from '@/contexts/theme-context';
import {
  isShiftPressed,
  Key,
} from '@platform/shortcuts/utils/keyboard-platform';
import {
  OrderEntryInputCellShortcutCallbacks,
  useOrderEntryCellShortcuts,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/use-order-entry-cell-shortcuts';
import { Platform, StyleSheet, TextInput, View } from 'react-native';
import {
  selectSalesPortalCartItem,
  useCartStore,
} from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { Tooltip } from '@usepepper/ui/atoms/tooltip';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useCellKeyboardNavigation } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/use-cell-keyboard-navigation';

interface OrderEntryTableQuantityCellProps {
  disabled?: boolean;
  /**
   * The maximum quantity of an item
   */
  maxQuantity: number;
  minQuantity: number;
  onSaveCart: () => void;
  /**
   * Rendered index (sorted position) passed from extended cell context.
   * This differs from TanStack's row.index which is the original data index.
   */
  rowIndex: number;
  shortcutCallbacks?: OrderEntryInputCellShortcutCallbacks;
  variantPackUUID: string;
}

// PERF: Memoized to prevent unnecessary re-renders when parent re-renders
export const OrderEntryTableQuantityCell =
  React.memo<OrderEntryTableQuantityCellProps>(
    ({
      disabled,
      maxQuantity,
      minQuantity,
      onSaveCart,
      rowIndex,
      shortcutCallbacks,
      variantPackUUID,
    }) => {
      const [styles, theme] = useStyles();
      const [isDirty, setIsDirty] = useState(false);

      // Each quantity cell subscribes only to whether THIS specific item is in the cart
      // This prevents unnecessary re-renders of other quantity cells
      const savedQuantity = useCartStore(
        (state) =>
          selectSalesPortalCartItem(state, variantPackUUID)?.quantity ?? 0,
      );

      const handleShortcutKeyPress =
        useOrderEntryCellShortcuts(shortcutCallbacks);

      // On native iOS, editable={false} prevents the TextInput from receiving
      // Tab focus via external keyboard. Keep it editable so Tab navigation
      // works, and use showSoftInputOnFocus to suppress the keyboard instead.
      const nativeEditable = Platform.OS !== 'web' || !disabled;

      const {
        handleBlurFocusCleanup,
        handleFocus,
        handleKeyPress,
        inputRef,
        isCellFocused,
      } = useCellKeyboardNavigation({
        columnId: ColumnId.Quantity,
        isDefaultColumn: true,
        // Row-0 boundary overrides: when the user is in the first row and
        // navigates upward (Shift+Tab or ArrowUp), focus the search input
        // instead of wrapping or staying put. These intentionally shadow the
        // default Tab/Arrow handlers in useCellKeyboardNavigation because the
        // desired behaviour at the table boundary differs from mid-table nav.
        onExtraKeyPress: (e, helpers) => {
          const key = e.nativeEvent.key;

          const isTab = key === Key.Tab;
          const isShiftTab = isTab && isShiftPressed(e);
          if (isShiftTab && rowIndex === 0) {
            e.preventDefault?.();
            helpers.clearFocus();
            helpers.blur();
            shortcutCallbacks?.onTriggerSearch?.();
            return true;
          }

          if (key === Key.ArrowUp && rowIndex === 0) {
            helpers.clearFocus();
            helpers.blur();
            shortcutCallbacks?.onTriggerSearch?.();
            return true;
          }

          return false;
        },
        onFocusExtra: () => {
          // Supplement selectTextOnFocus for tab navigation on iOS,
          // where the native prop doesn't fire for system-level focus changes.
          if (Platform.OS !== 'web' && inputRef.current && savedQuantity > 0) {
            inputRef.current.setSelection(0, savedQuantity.toString().length);
          }
        },
        onPreKeyPress: handleShortcutKeyPress,
        rowIndex,
      });

      // bind update function to this restaurant to prevent new function reference on every render
      const updateCartQuantity = useCallback(
        (quantity: number) =>
          useCartStore
            .getState()
            .updateCartItemQuantity(variantPackUUID, quantity),
        [variantPackUUID],
      );

      const handleChangeText = (text: string) => {
        if (disabled) {
          return;
        }
        // accept decimal and empty input (clear quantity)
        if (!validator.isDecimal(text) && text !== '') {
          return;
        }

        // update cart quantity if it has changed
        const updatedQuantity = text === '' ? 0 : validator.toFloat(text);
        if (updatedQuantity === savedQuantity) {
          return;
        }
        if (updateCartQuantity(updatedQuantity)) {
          setIsDirty(true);
        }
      };

      const handleBlur = useCallback(() => {
        handleBlurFocusCleanup();
        if (isDirty) {
          // Enforce the item's quantity constraints on commit
          const clampedQuantity = clampQuantityToLimits(
            savedQuantity,
            minQuantity,
            maxQuantity,
          );
          if (clampedQuantity !== savedQuantity) {
            updateCartQuantity(clampedQuantity);
          }
          setIsDirty(false);
          void onSaveCart();
        }
      }, [
        handleBlurFocusCleanup,
        isDirty,
        maxQuantity,
        minQuantity,
        onSaveCart,
        savedQuantity,
        updateCartQuantity,
      ]);

      const hasQuantityConstraints = minQuantity > 0 || maxQuantity > 0;
      const isTooltipVisible = isCellFocused && hasQuantityConstraints;

      return (
        <Tooltip
          containerStyle={styles.tooltipContainer}
          isOpen={isTooltipVisible}
          offset={{ y: 1 }}
          tabIndex={-1}
          trigger={
            <View style={styles.container}>
              <TextInput
                accessibilityState={{ disabled: !!disabled }}
                aria-disabled={disabled}
                editable={nativeEditable}
                keyboardType="decimal-pad"
                onBlur={handleBlur}
                onChangeText={handleChangeText}
                onFocus={handleFocus}
                onKeyPress={handleKeyPress}
                // workaround for react native bug where cursor appears in middle of placeholder
                // when using textAlign: 'center' on empty input. switching to space when focused
                // allows cursor to center properly. ref: https://github.com/facebook/react-native/issues/27658
                placeholder={isCellFocused ? ' ' : '0'}
                placeholderTextColor={theme.sk.color.text.hint}
                ref={inputRef}
                selectionColor={theme.sk.color.border.input.active}
                selectTextOnFocus
                showSoftInputOnFocus={!disabled}
                style={StyleSheet.flatten([
                  styles.quantityInput,
                  isCellFocused ? styles.quantityInputFocused : null,
                  disabled ? styles.disabled : null,
                ])}
                value={savedQuantity === 0 ? '' : savedQuantity.toString()}
              />
            </View>
          }
        >
          <Typography color={theme.sk.color.text.ondark} variant="paragraph-sm">
            {minQuantity > 0 ? (
              <>
                {i18n.t('sales_portal.order_entry.table.quantity_min')}{' '}
                <Typography
                  color={theme.sk.color.text.ondark}
                  variant="paragraph-sm-600"
                >
                  {minQuantity}
                </Typography>
              </>
            ) : null}
            {minQuantity > 0 && maxQuantity > 0 ? (
              <Typography
                color={theme.sk.color.text.ondark}
                variant="paragraph-sm-600"
              >
                {' • '}
              </Typography>
            ) : null}
            {maxQuantity > 0 ? (
              <>
                {i18n.t('sales_portal.order_entry.table.quantity_max')}{' '}
                <Typography
                  color={theme.sk.color.text.ondark}
                  variant="paragraph-sm-600"
                >
                  {maxQuantity}
                </Typography>
              </>
            ) : null}
          </Typography>
        </Tooltip>
      );
    },
  );

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'center',
    flex: 1,
  },
  disabled: {
    backgroundColor: theme.sk.color.bg.primary.disabled,
    color: theme.sk.color.text.disabled,
  },
  quantityInput: {
    ...theme.sk.typography.input.md,
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    color: theme.sk.color.text.primary,
    fontWeight: 400,
    height: 32,
    textAlign: 'center',
    width: 48,
  },
  quantityInputFocused: {
    borderColor: theme.sk.color.border.input.active,
  },
  tooltipContainer: {
    backgroundColor: theme.sk.color.bg.toast,
    borderWidth: 0,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceXsmall,
    shadowColor: '#000',
    shadowOffset: { height: 6, width: 0 },
    shadowOpacity: 0.05,
    shadowRadius: 12,
  },
}));
